import { useState, useEffect, useRef } from 'react';
import { usePage, router, Link } from '@inertiajs/react';
import { PageProps } from '@inertiajs/core';
import Helmet from 'react-helmet';
import {
  Container,
  Text,
  Button,
  Tabs,
  Group,
  Image,
  Title,
  Paper,
  Badge,
  Tooltip,
  ActionIcon,
  Box,
  Card,
  Modal,
  Grid,
  MantineProvider,
  Spoiler,
  Loader,
  Stack,
  SimpleGrid,
} from '@mantine/core';
import InteractionButtons from '../../components/Posts/InteractionButtons';
import { useScrollableTabs } from '../../src/hooks/useScrollableTabs';
import { useMediaQuery } from '@mantine/hooks';
import { Notifications, showNotification } from '@mantine/notifications';
import { useUser } from '../../src/context/UserContext';
import {
  IconCheck,
  IconWorld,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBrandYoutube,
  IconBrandTiktok,
  IconBrandSpotify,
  IconBrandSoundcloud,
  IconCalendarDown,
  IconCalendarUp,
  IconCalendarEvent,
  IconVideo,
  IconX,
  IconUserStar,
  IconSchool,
  IconBooks,
  IconChevronRight,
  IconMapPin,
} from '@tabler/icons-react';
import { StablePagination } from '../../components/StablePagination';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import EventCard from '../../components/Cards/EventCard';
import RecentSupporterCard from '../../components/Cards/RecentSupporterCard';
import VideoCard from '../../components/Cards/VideoCard';
import PrivateLessonPageCard from '../../components/Cards/PrivateLessonPageCard';
import PostCard from '../../components/Cards/ContentPostCard';
import ManageBadge from '../../components/ManageBadge';
import DynamicDrawer from '../../components/Drawer/DynamicDrawer';
import EventDrawerContent from '../../components/Drawer/EventDrawerContent';
import LoginModal from '../../components/LoginModal';
import useLoginModal from '../../hooks/useLoginModal';
import artistStarIcon from '../../assets/images/money-star-icon.png';

import '../../styles/ArtistShowView.css';
import '../../styles/ScrollTabs.css';

// Interfaces
interface User {
  id: number;
  roles?: string[];
  subscribed_artists?: Artist[];
  [key: string]: any;
}

interface Event {
  id: number;
  name: string;
  event_start_date: string;
  event_end_date: string;
  description: string;
  venue_name: string;
  address: string;
  city: string;
  state: string;
  ticket_url?: string;
  slug: string;
  banner_url?: string;
  event_types?: { id: number; name: string }[];
  [key: string]: any;
}

interface Artist {
  id: number;
  name: string;
  slug: string;
  description?: string;
  instructor: boolean;
  dj: boolean;
  artist_status_id: number;
  avatar_url?: string;
  area?: {
    id: number;
    name: string;
    slug: string;
    strip_area_suffix: boolean;
  };
  cropped_image?: string;
  website_url?: string;
  dance_with_me?: string;
  youtube_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  tiktok_url?: string;
  spotify_url?: string;
  soundcloud_url?: string;
  mixcloud_url?: string;
  dance_styles: DanceStyle[];
  is_portal_enabled: boolean;
  has_stripe_account: boolean;
  pending: boolean;
  status: string;
  subscribed_users_count: number;
  stars_count: number;
  [key: string]: any;
  upcoming_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
  past_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface DanceStyle {
  id: number;
  name: string;
  [key: string]: any;
}

interface Video {
  id: number;
  title: string;
  description: string;
  thumbnail_url: string;
  embed_url: string;
  video_type?: string;
  event?: Event;
  organizers?: any[];
  artists?: Artist[];
  dance_styles?: DanceStyle[];
}

interface StarPurchase {
  id: number;
  stars: number;
  user?: User;
  supporter_name?: string;
  message?: string;
  created_at: string;
}

interface PrivateLessonPage {
  id: number;
  description: string;
  timezone: string;
  event?: Event;
  artist: Artist;
  slug: string;
  time_slots: TimeSlot[];
}

interface TimeSlot {
  id: number;
  start_time: string;
  end_time: string;
  available: boolean;
}

interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  url: string;
  backgroundColor: string;
  borderColor: string;
}

interface CalendarMetadata {
  month: number;
  year: number;
}

// Cache interface for month-based calendar events
interface CalendarCache {
  [key: string]: CalendarEvent[];
}

interface Post {
  id: number;
  title: string;
  description: string;
  image_url?: string;
  slug: string;
}

interface ArtistShowProps extends PageProps {
  artist: Artist;
  upcoming_events: Event[];
  past_events: Event[];
  calendar_events: Event[];
  videos: Video[];
  private_lesson_pages: PrivateLessonPage[];
  posts: Post[];
  is_subscribed: boolean;
  current_user: User | null;
  calendar_metadata?: CalendarMetadata;
  star_purchases: StarPurchase[];
  upcoming_artist_events_count: number;
  past_artist_events_count: number;
  stripe_publishable_key?: string;
  page_meta?: {
    title: string;
    description: string;
    url: string;
    image: string;
  };
  upcoming_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
  past_pagination?: {
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

export default function ArtistShow() {
  const props = usePage<ArtistShowProps>().props;
  const {
    artist,
    calendar_events = [],
    videos = [],
    private_lesson_pages = [],
    posts = [],
    current_user,
    star_purchases = [],
    upcoming_artist_events_count,
    past_artist_events_count,
    stripe_publishable_key,
    page_meta,
    is_subscribed: initialIsSubscribed,
  } = usePage<ArtistShowProps>().props;

  const { user } = useUser() as { user: User | null; refreshUser: () => Promise<void> };

  // States
  const [activeTab, setActiveTab] = useState<string | null>('upcoming');
  const [upcomingPage, setUpcomingPage] = useState(1);

  const [pastPage, setPastPage] = useState(1);
  const [isSubscribed, setIsSubscribed] = useState(initialIsSubscribed);
  const [subscribersCount, setSubscribersCount] = useState(artist.subscribed_users_count);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [currentVideo, setCurrentVideo] = useState<Video | null>(null);
  const [calendarEvents, setCalendarEvents] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const upcoming_events = props.upcoming_events || [];
  const past_events = props.past_events || [];

  const calendar_metadata: CalendarMetadata = props.calendar_metadata || {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  };
  const upcoming_pagination = props.upcoming_pagination || { current_page: 1, per_page: 20, total_pages: 1 };
  const past_pagination = props.past_pagination || { current_page: 1, per_page: 20, total_pages: 1 };

  const [upcomingCurrentPage, setUpcomingCurrentPage] = useState(upcoming_pagination?.current_page || 1);
  const [pastCurrentPage, setPastCurrentPage] = useState(past_pagination?.current_page || 1);

  // Drawer states
  const [drawerOpened, setDrawerOpened] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);

  // Calendar-specific states
  const [eventsCache, setEventsCache] = useState<CalendarCache>({});
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState<number>(calendar_metadata.month);
  const [currentCalendarYear, setCurrentCalendarYear] = useState<number>(calendar_metadata.year);
  const calendarRef = useRef<any>(null);

  const ITEMS_PER_PAGE = 10;

  // At the top of your component, where you define hooks
  const { tabListReference: mobileTabListReference } = useScrollableTabs();
  const { tabListReference: desktopTabListReference } = useScrollableTabs();

  // Calculate paginated events
  const paginatedUpcomingEvents = upcoming_events.slice(
    (upcomingPage - 1) * ITEMS_PER_PAGE,
    upcomingPage * ITEMS_PER_PAGE,
  );

  const paginatedPastEvents = past_events.slice((pastPage - 1) * ITEMS_PER_PAGE, pastPage * ITEMS_PER_PAGE);

  const baseOnlyCounts = ['upcoming_total_count', 'past_total_count', 'artists_total_count', 'organizers_total_count'];

  const isAdmin = user?.roles?.includes('SuperAdmin');
  const isOwner = user?.id === artist.user;
  const canSeeStatus = isAdmin || isOwner;
  const isLoggedIn = !!user;

  // Create a cache key based on year and month
  const createCacheKey = (year: number, month: number) => {
    return `${year}-${month}`;
  };

  // Format events for calendar display
  const formatEventsForCalendar = (events: Event[]): CalendarEvent[] => {
    return events.map((event) => ({
      id: event.id?.toString() || `event-${Math.random().toString(36).substr(2, 9)}`,
      title: event.name || 'Untitled Event',
      start: event.event_start_date,
      end: event.event_end_date,
      url: event.slug ? `/events/${event.slug}` : '#',
      backgroundColor: '#ea580c',
      borderColor: '#ea580c',
    }));
  };

  const handleDatesSet = (dateInfo: any) => {
    const calendarDate = dateInfo.view.currentStart;
    const month = calendarDate.getMonth() + 1; // FullCalendar months are 0-indexed
    const year = calendarDate.getFullYear();

    // Only fetch if month/year has changed
    if (month !== currentCalendarMonth || year !== currentCalendarYear) {
      setCurrentCalendarMonth(month);
      setCurrentCalendarYear(year);

      const cacheKey = createCacheKey(year, month);
      // console.log('handleDatesSet:', cacheKey, 'cached?', !!eventsCache[cacheKey]);

      // Check if we already have events for this month in cache
      if (eventsCache[cacheKey]) {
        setCalendarEvents(eventsCache[cacheKey]);
      } else {
        // Fetch events for this month from server
        fetchCalendarEvents(month, year);
      }
    }
  };

  // Fetch calendar events for a specific month and year
  const fetchCalendarEvents = (month: number, year: number) => {
    setIsLoading(true);

    const cacheKey = createCacheKey(year, month);

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'calendar',
        month: month,
        year: year,
      },
      preserveState: true,
      preserveScroll: true,
      only: ['calendar_events', 'calendar_metadata', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  useEffect(() => {
    // If you need to manually update the document title and meta tags
    if (page_meta?.title) {
      document.title = page_meta.title;
    }

    console.log('Artist Page Meta:', page_meta);
    // console.log('✅✅✅ Artist Area:', artist.area);
    // console.log('✅✅✅ Artist Area:', artist.area?.slug);

    console.log('Artist Avatar URL:', artist.avatar_url);

    // Optional: Update meta description directly if Helmet doesn't work
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription && page_meta?.description) {
      metaDescription.setAttribute('content', page_meta.description);
    }
  }, [page_meta]);

  // only re-run when the count actually differs
  useEffect(() => {
    if (!calendar_events) return;

    const formatted = calendar_events.map((evt) => ({
      id: evt.id?.toString() || `event-${Math.random().toString(36).slice(2)}`,
      title: evt.name || 'Untitled',
      start: evt.event_start_date,
      end: evt.event_end_date,
      url: evt.slug ? `/events/${evt.slug}` : null,
    }));

    setCalendarEvents(formatted);
  }, [calendar_events.length]);

  // Load FullCalendar CSS
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://cdn.jsdelivr.net/npm/fullcalendar@5.11.3/main.min.css';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  const handlePastPageChange = (page: number) => {
    setIsLoading(true);
    setPastCurrentPage(page);

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'past',
        past_page: page,
      },
      preserveState: true,
      only: ['past_events', 'past_pagination', 'past_total_count', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  const handleUpcomingPageChange = (page: number) => {
    setIsLoading(true);
    setUpcomingCurrentPage(page);

    router.visit(window.location.pathname, {
      data: {
        active_tab: 'upcoming',
        upcoming_page: page,
      },
      preserveState: true,
      only: ['upcoming_events', 'upcoming_pagination', 'upcoming_total_count', ...baseOnlyCounts],
      onFinish: () => {
        setIsLoading(false);
      },
    });
  };

  function handleTabChange(tab: string | null) {
    if (!tab) return;

    setActiveTab(tab);
    setIsLoading(true);

    // 1) Base props sent on every tab change
    const onlyProps = [
      'active_tab',
      'upcoming_artist_events_count',
      'past_artist_events_count',
      'videos_count',
      'private_lesson_pages_count',
      'posts_count',
      'star_purchases_count',
    ];

    // 2) Add tab-specific keys
    if (tab === 'upcoming') {
      onlyProps.push('upcoming_events', 'upcoming_pagination');
    } else if (tab === 'past') {
      onlyProps.push('past_events', 'past_pagination');
    } else if (tab === 'calendar') {
      onlyProps.push('calendar_events', 'calendar_metadata');
    } else if (tab === 'videos') {
      onlyProps.push('videos', 'videos_pagination');
    } else if (tab === 'supporters') {
      onlyProps.push('star_purchases', 'supporters_pagination');
    } else if (tab === 'lessons') {
      onlyProps.push('private_lesson_pages');
    } else if (tab === 'content') {
      onlyProps.push('posts', 'content_pagination');
    }

    // 3) Build query params
    const data: Record<string, any> = { active_tab: tab };

    // reset pagination when switching event tabs
    if (tab === 'upcoming') data.upcoming_page = 1;
    if (tab === 'past') data.past_page = 1;

    // 4) Trigger Inertia visit with inline URL
    router.visit(
      `/artists/${artist.slug}`, // ← inline path here
      {
        data,
        only: onlyProps,
        preserveState: true,
        preserveScroll: true,
        onFinish: () => setIsLoading(false),
      },
    );
  }

  // Handle subscription action when user is authenticated
  const handleSubscriptionAuthenticated = async () => {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
    const endpoint = isSubscribed ? `/artists/${artist.slug}/unsubscribe` : `/artists/${artist.slug}/subscribe`;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken || '',
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();

        // Update state based on server response
        setIsSubscribed(data.is_subscribed);
        setSubscribersCount(data.subscribers_count);

        // Show notification
        showNotification({
          color: 'green',
          title: data.is_subscribed ? 'Subscribed' : 'Unsubscribed',
          message: data.message,
          icon: <IconCheck />,
          autoClose: 3000,
        });
      } else {
        showNotification({
          color: 'red',
          title: 'Error',
          message: 'Something went wrong',
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error('Error handling subscription:', error);
      showNotification({
        color: 'red',
        title: 'Error',
        message: 'Failed to process your request',
        icon: <IconX />,
        autoClose: 3000,
      });
    }
  };

  // Initialize the login modal hook
  const { isLoginModalOpen, loginModalProps, requireLogin, closeLoginModal } = useLoginModal({
    onAuthenticatedAction: handleSubscriptionAuthenticated,
  });

  // Handle video modal
  const openVideoModal = (video: Video) => {
    setCurrentVideo(video);
    setVideoModalOpen(true);
  };

  // Handle drawer functions
  const handleEventClick = (event: Event) => {
    setSelectedEvent(event);
    setDrawerOpened(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpened(false);
  };

  const isMobile = useMediaQuery('(max-width: 768px)');
  const userHasArtistAccess = user && (user.roles?.includes('SuperAdmin') || artist.user_id === user.id);

  // Render status badge

  // Create content for different sections

  const manageBadgeContent = (
    <ManageBadge entityType="artist" entitySlug={artist.slug} userHasAccess={userHasArtistAccess} />
  );

  const getArtistImage = () => {
    if (artist.cropped_image) {
      return artist.cropped_image;
    } else if (artist.avatar_url) {
      return artist.avatar_url;
    } else {
      return 'https://dancewise.s3.amazonaws.com/misc-images/Blank+Avatar.png';
    }
  };

  const areaName = artist.area
    ? artist.area.strip_area_suffix
      ? // remove trailing “Area” (with optional preceding space), case‑insensitive
        artist.area.name.replace(/\s*\bArea\b\s*$/i, '')
      : artist.area.name
    : '';

  const token = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content;

  const imageContent = (
    <>
      <Stack gap="xs" mb="xs">
        {canSeeStatus && isLoggedIn && (
          <Group align="center" gap="md" mb="sm">
            {/* Status Badge */}
            {(() => {
              const meta = {
                1: { label: 'Active', color: 'green' },
                2: { label: 'Pending', color: 'yellow' },
                3: { label: 'Rejected', color: 'red' },
              }[artist.artist_status_id] || { label: 'Unknown', color: 'gray' };

              return (
                <Badge color={meta.color} size="lg" variant="light" style={{ textTransform: 'none' }}>
                  Artist Status: {meta.label}
                </Badge>
              );
            })()}

            {/* Approve Button, only for admins & pending */}
            {isAdmin && artist.artist_status_id === 2 && (
              <Button
                color="green"
                variant="light"
                fullWidth
                size="lg"
                radius="md"
                onClick={() =>
                  router.put(
                    `/artists/${artist.slug}/approve`,
                    {},
                    {
                      preserveScroll: true,
                      headers: {
                        'X-CSRF-Token': token || '',
                      },
                      onSuccess: () => {
                        showNotification({
                          title: 'Success',
                          message: `Event "${artist.name}" has been approved!`,
                          color: 'green',
                          icon: <IconCheck />, // You might need to import this
                          autoClose: 5000,
                          position: 'top-right',
                        });
                      },
                      onError: (error) => {
                        showNotification({
                          title: 'Error',
                          message: `Failed to approve event: ${error.message || 'Unknown error'}`,
                          color: 'red',
                          icon: <IconX />,
                          autoClose: 5000,
                        });
                      },
                    },
                  )
                }
              >
                Approve This Artist
              </Button>
            )}
          </Group>
        )}

        {artist?.area && (
          <Link href={`/city/${artist?.area?.slug}`} className="inline-flex" style={{ textDecoration: 'none' }}>
            <Button
              component="button"
              variant="filled"
              color="#EA580C"
              radius="md"
              size="xs"
              styles={{
                root: {
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginBottom: '4px',
                  width: 'fit-content',
                },
                leftIcon: { marginRight: 4 },
                rightIcon: { marginLeft: 4 },
                label: { fontSize: '0.875rem', fontWeight: 500 },
              }}
              leftSection={<IconMapPin size={14} />}
              rightSection={<IconChevronRight size={14} />}
            >
              Listed in {areaName}
            </Button>
          </Link>
        )}

        <Image
          src={getArtistImage()}
          alt={artist.name}
          radius="sm"
          mb="0"
          style={{ height: '100%' }}
          w="100%"
          className="artist-image"
        />
      </Stack>
    </>
  );

  const titleContent = (
    <Title fw={700} className="rubik mb-2" style={{ color: '#F2F2F2', lineHeight: '1' }}>
      {artist.name}
    </Title>
  );

  const artistTypeContent = (
    <Group gap="0">
      {artist.instructor && (
        <Badge
          color="gray"
          size="sm"
          variant="filled"
          style={{
            color: '#171717',
            backgroundColor: '#f2f2f2',
            border: '2px solid #FFF7ED',
            fontSize: '0.9rem',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
            textTransform: 'none',
          }}
        >
          Instructor
        </Badge>
      )}
      {artist.dj && (
        <Badge
          color="gray"
          size="sm"
          variant="filled"
          style={{
            color: '#171717',
            backgroundColor: '#f2f2f2',
            border: '2px solid #FFF7ED',
            fontSize: '0.9rem',
            boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
          }}
        >
          DJ
        </Badge>
      )}
    </Group>
  );

  const danceStylesContent =
    artist.dance_styles && artist.dance_styles.length > 0 ? (
      <Group mt="xs" gap="xs">
        {artist.dance_styles.map((style) => (
          <Badge key={style.id} color="#EA580C" size="md" className="tag-badge">
            {style.name}
          </Badge>
        ))}
      </Group>
    ) : null;

  const socialLinksContent = (
    <>
      <Group mt="4px" mb="4px">
        {artist.dance_with_me && (
          <Tooltip label="Dance With Me">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.dance_with_me} target="_blank">
              <Image src="/images/domain-logo.png" alt="Dance With Me" width={20} height={20} />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.website_url && (
          <Tooltip label="Website">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.website_url} target="_blank">
              <IconWorld size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.youtube_url && (
          <Tooltip label="YouTube">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.youtube_url} target="_blank">
              <IconBrandYoutube size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.facebook_url && (
          <Tooltip label="Facebook">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.facebook_url} target="_blank">
              <IconBrandFacebook size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.instagram_url && (
          <Tooltip label="Instagram">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.instagram_url} target="_blank">
              <IconBrandInstagram size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.tiktok_url && (
          <Tooltip label="TikTok">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.tiktok_url} target="_blank">
              <IconBrandTiktok size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.spotify_url && (
          <Tooltip label="Spotify">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.spotify_url} target="_blank">
              <IconBrandSpotify size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.soundcloud_url && (
          <Tooltip label="SoundCloud">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.soundcloud_url} target="_blank">
              <IconBrandSoundcloud size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
        {artist.mixcloud_url && (
          <Tooltip label="Mixcloud">
            <ActionIcon variant="subtle" size="lg" component="a" href={artist.mixcloud_url} target="_blank">
              <IconBrandSoundcloud size={20} color="orange" />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
      <Box w="100%" my="8px">
        <InteractionButtons context={['artist']} isUserSignedIn={isLoggedIn} object={artist} />
      </Box>
    </>
  );

  const subscribersContent = (
    <Text mt="xs" style={{ color: '#FFF7ED' }}>
      Subscribers: {subscribersCount}
    </Text>
  );

  const subscribeButtonContent = (
    <Button
      variant={isSubscribed ? 'light' : 'filled'}
      onClick={() =>
        requireLogin({
          actionText: 'to subscribe',
          entityName: artist.name,
        })
      }
      color="#EA580C"
      fullWidth
      className="clear-button"
      disabled
      mt="xs"
    >
      {isSubscribed ? 'Subscribed' : 'Subscribe'}
    </Button>
  );

  const extraButtonsContent = (
    <Box mt="4px">
      {/* {artist.is_portal_enabled && (
        <Button
          component="a"
          href={`/artists/${artist.slug}/portal`}
          variant="filled"
          color="#EA580C"
          fullWidth
          mb="sm"
        >
          View Booking Portal
        </Button>
      )} */}

      {artist.has_stripe_account && (
        <Button
          variant="light"
          color="#EA580C"
          leftSection={<Image src={artistStarIcon} className="star-icon" alt="Support" />}
          fullWidth
          disabled
          className="support-button"
          mb="xs"
          mt="4px"
          size="lg"
          onClick={() =>
            requireLogin({
              actionText: 'to support',
              entityName: artist.name,
            })
          }
        >
          Support
        </Button>
      )}

      <Group mt="xs" mb="xs" justify="flex-start">
        <Badge size="xl" variant="filled" color="orange">
          <Group gap="xs">
            <Image src={artistStarIcon} alt="Stars" className="star-icon" />
            <Text fw={600}>{artist.stars_count || 0}</Text>
          </Group>
        </Badge>
      </Group>

      {user && user.roles?.includes('SuperAdmin') && artist.pending && (
        <Button variant="filled" color="green" fullWidth mt="md" component="a" href={`/artists/${artist.slug}/approve`}>
          Approve This Artist
        </Button>
      )}
    </Box>
  );

  const bioContent = (
    <>
      <Title
        order={4}
        fw={700}
        mt="xl"
        style={{ color: '#FFF7ED', fontWeight: 'bold', marginBottom: 0 }}
        className="poppins"
      >
        Artist Bio
      </Title>
      <Spoiler maxHeight={100} mb="4rem" mt="xs" showLabel="Read more..." hideLabel="Show less" color="orange">
        <Text
          className="artist-text"
          dangerouslySetInnerHTML={{ __html: artist.description || '' }}
          style={{ color: '#FFF7ED', fontSize: '0.8rem', fontFamily: 'Poppins' }}
        />
      </Spoiler>
    </>
  );

  const desktopTabsContent = (
    <Tabs value={activeTab} onChange={handleTabChange} mt="xl" color="orange" className="desktop-tabs">
      <Tabs.List ref={desktopTabListReference}>
        <Tabs.Tab
          value="upcoming"
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
          leftSection={<IconCalendarUp size={20} stroke={1.5} color="#FFF7ED" />}
          rightSection={
            <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
              {upcoming_artist_events_count}
            </Badge>
          }
        >
          Upcoming Events
        </Tabs.Tab>

        <Tabs.Tab
          value="past"
          leftSection={<IconCalendarDown size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
          rightSection={
            <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
              {past_artist_events_count}
            </Badge>
          }
        >
          Past Events
        </Tabs.Tab>

        <Tabs.Tab
          value="calendar"
          leftSection={<IconCalendarEvent size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Calendar View
        </Tabs.Tab>

        <Tabs.Tab
          value="videos"
          leftSection={<IconVideo size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Videos
        </Tabs.Tab>

        <Tabs.Tab
          value="supporters"
          leftSection={<Image src={artistStarIcon} className="star-icon" alt="Recent Supporters" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Recent Supporters
        </Tabs.Tab>

        <Tabs.Tab
          value="lessons"
          leftSection={<IconSchool size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Private Lessons
        </Tabs.Tab>

        <Tabs.Tab
          value="content"
          leftSection={<IconBooks size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Content
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="upcoming" pt="md">
        {upcoming_artist_events_count > 0 ? (
          <>
            <Group my="sm">
              {upcoming_pagination && upcoming_pagination.total_pages > 1 && (
                <StablePagination
                  total={upcoming_pagination.total_pages}
                  value={upcomingPage}
                  onChange={handleUpcomingPageChange}
                  color="#ea580c"
                />
              )}
            </Group>

            <Grid gutter="8px">
              {paginatedUpcomingEvents.map((event) => (
                <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
                  <EventCard event={event} onCardClick={handleEventClick} />
                </Grid.Col>
              ))}
            </Grid>

            {upcoming_artist_events_count > 10 && (
              <Group my="sm">
                <StablePagination
                  total={upcoming_pagination.total_pages}
                  value={upcomingCurrentPage}
                  onChange={handleUpcomingPageChange}
                  color="#ea580c"
                />
              </Group>
            )}
          </>
        ) : (
          <Text size="xl" fw={500} mt={3} style={{ color: '#FFF7ED', fontWeight: 'bold' }} ta="center">
            No upcoming events
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="past" pt="md">
        <Group justify="flex-start" mb="md">
          <Text className="text-white poppins">
            Showing {past_events.length} of {past_artist_events_count} past events
            {isLoading && <Loader color="orange" size="xs" ml="md" />}
          </Text>
        </Group>
        {past_artist_events_count > 0 ? (
          <>
            <Group my="sm">
              {past_artist_events_count > 10 && (
                <StablePagination
                  total={past_pagination.total_pages}
                  value={pastCurrentPage}
                  onChange={handlePastPageChange}
                  color="#ea580c"
                />
              )}
            </Group>

            <Grid gutter="8px">
              {paginatedPastEvents.map((event) => (
                <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
                  <EventCard event={event} onCardClick={handleEventClick} />
                </Grid.Col>
              ))}
            </Grid>

            {past_artist_events_count > 10 && (
              <Group my="sm">
                <StablePagination
                  total={past_pagination.total_pages}
                  value={pastCurrentPage}
                  onChange={handlePastPageChange}
                  color="#ea580c"
                />
              </Group>
            )}
          </>
        ) : (
          <Text ta="center" mt="md">
            No past events
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="calendar" pt="md">
        <Box mt="md" style={{ height: 'auto' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            themeSystem="standard"
            height="auto"
            datesSet={handleDatesSet}
            events={calendarEvents}
            displayEventTime={false}
            eventClick={(info) => {
              // Prevent default navigation
              info.jsEvent.preventDefault();

              // Extract slug from the URL
              let eventSlug = null;
              if (info.event.url) {
                // Get the last part of the URL which should be the slug
                const urlParts = info.event.url.split('/');
                eventSlug = urlParts[urlParts.length - 1];
              }

              if (!eventSlug) {
                console.error('Could not extract slug from URL:', info.event.url);
                return;
              }

              // First check in paginated events (these have complete data)
              const paginatedEvents = [...upcoming_events, ...past_events];
              let fullEvent = paginatedEvents.find((e) => e.slug === eventSlug);

              // If not found in paginated events, check in all_calendar_events
              if (!fullEvent) {
                fullEvent = calendar_events.find((e) => e.slug === eventSlug);
              }

              if (fullEvent) {
                // Call the handler with the complete event object
                handleEventClick(fullEvent);
              } else {
                console.error('No matching event found for slug:', eventSlug);
              }
            }}
          />
        </Box>
      </Tabs.Panel>

      <Tabs.Panel value="videos" pt="md">
        {videos && videos.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="8px">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} onWatch={openVideoModal} />
            ))}
          </SimpleGrid>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            No videos available.
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="supporters" pt="md">
        {star_purchases && star_purchases.length > 0 ? (
          <Stack gap="sm">
            {star_purchases.slice(0, 10).map((purchase) => (
              <RecentSupporterCard key={purchase.id} purchase={purchase} />
            ))}
          </Stack>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            {artist.name} hasn't received any stars yet, you can support them with artist stars by clicking on the
            support button on their profile!
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="lessons" pt="md">
        {private_lesson_pages && private_lesson_pages.length > 0 ? (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="8px">
            {private_lesson_pages.map((page) => (
              <PrivateLessonPageCard key={page.id} page={page} artistSlug={artist.slug} />
            ))}
          </SimpleGrid>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            No private lesson pages available.
          </Text>
        )}
      </Tabs.Panel>

      {/* other tabs */}

      <Tabs.Panel value="content" pt="md">
        {posts && posts.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="8px">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </SimpleGrid>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            This artist doesn't have any linked content yet!
          </Text>
        )}
      </Tabs.Panel>
    </Tabs>
  );

  const mobileTabsContent = (
    <Tabs value={activeTab} onChange={handleTabChange} mt="xl" color="orange" className="mobile-tabs">
      <Tabs.List ref={mobileTabListReference}>
        <Tabs.Tab
          value="upcoming"
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
          leftSection={<IconCalendarUp size={20} stroke={1.5} color="#FFF7ED" />}
          rightSection={
            <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
              {upcoming_artist_events_count}
            </Badge>
          }
        >
          Upcoming Events
        </Tabs.Tab>

        <Tabs.Tab
          value="past"
          leftSection={<IconCalendarDown size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
          rightSection={
            <Badge variant="light" className="badge" style={{ minWidth: '40px', minHeight: '30px' }}>
              {past_artist_events_count}
            </Badge>
          }
        >
          Past Events
        </Tabs.Tab>

        <Tabs.Tab
          value="calendar"
          leftSection={<IconCalendarEvent size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Calendar View
        </Tabs.Tab>

        <Tabs.Tab
          value="videos"
          leftSection={<IconVideo size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Videos
        </Tabs.Tab>

        <Tabs.Tab
          value="supporters"
          leftSection={<Image src={artistStarIcon} className="star-icon" alt="Recent Supporters" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Recent Supporters
        </Tabs.Tab>

        <Tabs.Tab
          value="lessons"
          leftSection={<IconSchool size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Private Lessons
        </Tabs.Tab>

        <Tabs.Tab
          value="content"
          leftSection={<IconBooks size={20} stroke={1.5} color="#FFF7ED" />}
          style={{ color: '#FFF7ED', fontWeight: 'bold' }}
        >
          Content
        </Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="upcoming" pt="md">
        {upcoming_artist_events_count > 0 ? (
          <>
            <Group my="sm">
              {upcoming_pagination && upcoming_pagination.total_pages > 1 && (
                <StablePagination
                  total={upcoming_pagination.total_pages}
                  value={upcomingPage}
                  onChange={handleUpcomingPageChange}
                  color="#ea580c"
                />
              )}
            </Group>

            <Grid gutter="8px">
              {paginatedUpcomingEvents.map((event) => (
                <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
                  <EventCard event={event} onCardClick={handleEventClick} />
                </Grid.Col>
              ))}
            </Grid>

            {upcoming_artist_events_count > 10 && (
              <Group my="sm">
                <StablePagination
                  total={upcoming_pagination.total_pages}
                  value={upcomingCurrentPage}
                  onChange={handleUpcomingPageChange}
                  color="#ea580c"
                />
              </Group>
            )}
          </>
        ) : (
          <Text size="xl" fw={500} mt={3} style={{ color: '#FFF7ED', fontWeight: 'bold' }} ta="center">
            No upcoming events
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="past" pt="md">
        <Group justify="flex-start" mb="md">
          <Text className="text-white poppins">
            Showing {past_events.length} of {past_artist_events_count} past events
            {isLoading && <Loader color="orange" size="xs" ml="md" />}
          </Text>
        </Group>
        {past_artist_events_count > 0 ? (
          <>
            <Group my="sm">
              {past_artist_events_count > 10 && (
                <StablePagination
                  total={past_pagination.total_pages}
                  value={pastCurrentPage}
                  onChange={handlePastPageChange}
                  color="#ea580c"
                />
              )}
            </Group>

            <Grid gutter="8px">
              {paginatedPastEvents.map((event) => (
                <Grid.Col span={{ base: 12, sm: 6, lg: 4 }} key={event.id}>
                  <EventCard event={event} onCardClick={handleEventClick} />
                </Grid.Col>
              ))}
            </Grid>

            {past_artist_events_count > 10 && (
              <Group my="sm">
                <StablePagination
                  total={past_pagination.total_pages}
                  value={pastCurrentPage}
                  onChange={handlePastPageChange}
                  color="#ea580c"
                />
              </Group>
            )}
          </>
        ) : (
          <Text ta="center" mt="md">
            No past events
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="calendar" pt="md">
        <Box mt="md" style={{ height: 'auto' }}>
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            themeSystem="standard"
            height="auto"
            datesSet={handleDatesSet}
            events={calendarEvents}
            displayEventTime={false}
            eventClick={(info) => {
              // Prevent default navigation
              info.jsEvent.preventDefault();

              // Extract slug from the URL
              let eventSlug = null;
              if (info.event.url) {
                // Get the last part of the URL which should be the slug
                const urlParts = info.event.url.split('/');
                eventSlug = urlParts[urlParts.length - 1];
              }

              if (!eventSlug) {
                console.error('Could not extract slug from URL:', info.event.url);
                return;
              }

              // First check in paginated events (these have complete data)
              const paginatedEvents = [...upcoming_events, ...past_events];
              let fullEvent = paginatedEvents.find((e) => e.slug === eventSlug);

              // If not found in paginated events, check in all_calendar_events
              if (!fullEvent) {
                fullEvent = calendar_events.find((e) => e.slug === eventSlug);
              }

              if (fullEvent) {
                // Call the handler with the complete event object
                handleEventClick(fullEvent);
              } else {
                console.error('No matching event found for slug:', eventSlug);
              }
            }}
          />
        </Box>
      </Tabs.Panel>

      <Tabs.Panel value="videos" pt="md">
        {videos && videos.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="8px">
            {videos.map((video) => (
              <VideoCard key={video.id} video={video} onWatch={openVideoModal} />
            ))}
          </SimpleGrid>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            No videos available.
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="supporters" pt="md">
        {star_purchases && star_purchases.length > 0 ? (
          <Stack gap="sm">
            {star_purchases.slice(0, 10).map((purchase) => (
              <RecentSupporterCard key={purchase.id} purchase={purchase} />
            ))}
          </Stack>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            {artist.name} hasn't received any stars yet, you can support them with artist stars by clicking on the
            support button on their profile!
          </Text>
        )}
      </Tabs.Panel>

      <Tabs.Panel value="lessons" pt="md">
        {private_lesson_pages && private_lesson_pages.length > 0 ? (
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="8px">
            {private_lesson_pages.map((page) => (
              <PrivateLessonPageCard key={page.id} page={page} artistSlug={artist.slug} />
            ))}
          </SimpleGrid>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            No private lesson pages available.
          </Text>
        )}
      </Tabs.Panel>

      {/* other tabs */}

      <Tabs.Panel value="content" pt="md">
        {posts && posts.length > 0 ? (
          <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="8px">
            {posts.map((post) => (
              <PostCard key={post.id} post={post} />
            ))}
          </SimpleGrid>
        ) : (
          <Text ta="left" mt="md" className="text-white">
            This artist doesn't have any linked content yet!
          </Text>
        )}
      </Tabs.Panel>
    </Tabs>
  );

  return (
    <MantineProvider>
      {page_meta && (
        <Helmet>
          <title>{page_meta.title}</title>
          <meta name="description" content={page_meta.description} />

          {/* Open Graph tags */}
          <meta property="og:title" content={page_meta.title} />
          <meta property="og:description" content={page_meta.description} />
          <meta property="og:type" content="website" />
          <meta property="og:url" content={page_meta.url} />
          <meta property="og:image" content={page_meta.image} />

          {/* Twitter Card tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={page_meta.title} />
          <meta name="twitter:description" content={page_meta.description} />
          <meta name="twitter:image" content={page_meta.image} />
        </Helmet>
      )}
      <Container size="xl" mb="8rem" style={{ minHeight: '100vh' }} px={isMobile ? 0 : undefined}>
        <Notifications position="top-right" zIndex={2077} />

        <Paper
          p="lg"
          shadow="xs"
          mt="lg"
          style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }}
          pos="relative"
          w="100%"
        >
          {/* Two-column layout container */}
          <div className="artist-container">
            {/* Mobile view - sections directly in container for ordering */}
            <div className="artist-mobile">
              <div className="artist-section artist-manage">{manageBadgeContent}</div>
              <div className="artist-section artist-image">{imageContent}</div>
              <div className="artist-section artist-title">{titleContent}</div>
              <div className="artist-section artist-type">{artistTypeContent}</div>
              <div className="artist-section artist-dance-styles">{danceStylesContent}</div>
              <div className="artist-section artist-social">{socialLinksContent}</div>
              <div className="artist-section artist-subscribers">{subscribersContent}</div>
              <div className="artist-section artist-subscribe">{subscribeButtonContent}</div>
              <div className="artist-section artist-extra-buttons">{extraButtonsContent}</div>
              <div className="artist-section artist-bio">{bioContent}</div>
              <div className="artist-section artist-tabs">{desktopTabsContent}</div>
            </div>

            {/* Desktop view - two column layout */}
            <div className="artist-sidebar">
              <div className="artist-section artist-manage">{manageBadgeContent}</div>
              <div className="artist-section artist-image">{imageContent}</div>
              <div className="artist-section artist-title">{titleContent}</div>
              <div className="artist-section artist-type">{artistTypeContent}</div>
              <div className="artist-section artist-dance-styles">{danceStylesContent}</div>
              <div className="artist-section artist-social">{socialLinksContent}</div>
              <div className="artist-section artist-subscribers">{subscribersContent}</div>
              <div className="artist-section artist-subscribe">{subscribeButtonContent}</div>
              <div className="artist-section artist-extra-buttons">{extraButtonsContent}</div>
              <div className="artist-section artist-bio">{bioContent}</div>
            </div>

            <div className="artist-main">
              <div className="artist-section artist-tabs">{mobileTabsContent}</div>
            </div>
          </div>
        </Paper>

        {/* Video Modal */}
        <Modal
          opened={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          title={currentVideo?.title || 'Video'}
          size="xl"
          centered
        >
          {currentVideo && (
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0 }}>
              <iframe
                src={currentVideo.embed_url}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          )}
        </Modal>

        {/* Login Modal */}
        <LoginModal
          opened={isLoginModalOpen}
          onClose={closeLoginModal}
          actionText={loginModalProps.actionText}
          entityName={loginModalProps.entityName}
          returnPath={loginModalProps.returnPath}
        />

        {/* Dynamic Drawer */}
        <DynamicDrawer opened={drawerOpened} type="event" data={selectedEvent} onClose={handleCloseDrawer} />
      </Container>
    </MantineProvider>
  );
}
